import { useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select, Spin } from 'antd';
import { HomeOutlined, NumberOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { lookupZipCode } from '@/utils/address';
import { useIsMobile } from '@/context/IsMobileContext.tsx';

// US States list
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface AddressFieldsProps {
  form: FormInstance;
  required?: boolean;
  disabled?: boolean;
  /**
   * Decorative house / hash glyphs inside the inputs. They repeat what the
   * label already says, so surfaces that have room for proper labels turn
   * them off. Kept on by default for the admin modals that rely on them.
   */
  showFieldIcons?: boolean;
}

export const AddressFields = ({
  form,
  required = false,
  disabled = false,
  showFieldIcons = true,
}: AddressFieldsProps) => {
  const { isMobile } = useIsMobile();
  const [isLookingUp, setIsLookingUp] = useState(false);

  const handleZipCodeChange = async (zipCode: string) => {
    // Only lookup if we have a valid 5-digit zip code
    if (/^\d{5}$/.test(zipCode)) {
      setIsLookingUp(true);
      const result = await lookupZipCode(zipCode);
      setIsLookingUp(false);

      if (result) {
        form.setFieldsValue({
          property_city: result.city,
          property_state: result.stateAbbr,
        });
      }
    }
  };

  // Watch for zip code changes
  const zipCode = Form.useWatch('property_zip_code', form);
  useEffect(() => {
    if (zipCode && /^\d{5}$/.test(zipCode)) {
      const currentCity = form.getFieldValue('property_city');
      const currentState = form.getFieldValue('property_state');
      // Only auto-populate if city and state are empty
      if (!currentCity && !currentState) {
        handleZipCodeChange(zipCode);
      }
    }
  }, [zipCode]);

  return (
    <>
      <Row gutter={isMobile ? 16 : 24}>
        <Col span={24} md={16}>
          <Form.Item
            name='property_address'
            label='Street Address'
            rules={required ? [{ required: true, message: 'Please enter street address' }] : []}
          >
            <Input
              prefix={showFieldIcons ? <HomeOutlined /> : undefined}
              placeholder='123 Main Street'
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={8}>
          <Form.Item name='property_apt_unit' label='Apt/Unit'>
            <Input
              prefix={showFieldIcons ? <NumberOutlined /> : undefined}
              placeholder='Apt 5B'
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={isMobile ? 16 : 24}>
        <Col span={24} md={10}>
          <Form.Item
            name='property_city'
            label='City'
            rules={required ? [{ required: true, message: 'Please enter city' }] : []}
          >
            <Input placeholder='New York' disabled={disabled} />
          </Form.Item>
        </Col>
        <Col span={24} md={8}>
          <Form.Item
            name='property_state'
            label='State'
            rules={required ? [{ required: true, message: 'Please select state' }] : []}
          >
            <Select
              placeholder='Select state'
              options={US_STATES}
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase()) ||
                (option?.value?.toLowerCase() ?? '').includes(input.toLowerCase())
              }
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={6}>
          <Form.Item
            name='property_zip_code'
            label='ZIP Code'
            rules={required ? [{ required: true, message: 'Please enter ZIP code' }] : []}
          >
            <Input
              placeholder='10001'
              maxLength={5}
              disabled={disabled}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                form.setFieldValue('property_zip_code', value);
                if (value.length === 5) {
                  handleZipCodeChange(value);
                }
              }}
              suffix={isLookingUp ? <Spin size='small' /> : null}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
